import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectOnboarding from '@/components/ProjectOnboarding';
import { useOrganization } from '@/contexts/OrganizationContext';
import useApiFetch from '@/hooks/useApiFetch';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { supabase } from '@/lib/supabase';
import googleAnalyticsService, { GA4Property } from '@/services/googleAnalytics';
import searchConsoleService, {
  GoogleConnectionStatus,
  SearchConsoleProperty,
} from '@/services/searchConsole';
import seoProjectsService from '@/services/seoProjects';
import { FetchApiIds } from '@/types';
import { SeoProject } from '@/types/api/seo';
import SEOxProjectListComponent from './SEOxProjectList.component';

type ProjectsPaginated = {
  data: SeoProject[];
  count: number;
  currentPage: number;
  lastPage: number;
  nextPage: number | null;
  prevPage: number | null;
};

const SEOxProjectListContainer = function (): JSX.Element {
  const navigate = useNavigate();
  const { activeOrgId } = useOrganization();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { data: projectsData, isLoading: isLoadingProjects } = useSeoFetch<ProjectsPaginated>(
    [FetchApiIds.SEO_PROJECTS],
    () => seoProjectsService.getAll({ page: 1, take: 50 }),
    { retry: false }
  );

  const projects = useMemo(() => projectsData?.data ?? [], [projectsData]);

  // Lightweight DB-only check -- no edge function call
  const { data: connectionStatus, isLoading: isLoadingConnection } =
    useSeoFetch<GoogleConnectionStatus>(
      [FetchApiIds.SEO_GOOGLE_CONNECTION],
      () => searchConsoleService.getConnectionStatus(),
      { retry: false }
    );

  const isConnected = !!connectionStatus?.isConnected;

  const showWizardOrOnboarding = (!isLoadingProjects && projects.length === 0) || showOnboarding;

  // Only fetch available sites + GA4 properties when onboarding is shown (needs edge function)
  const { data: availableSites = [], isLoading: isLoadingSites } = useSeoFetch<
    SearchConsoleProperty[]
  >(['seo-available-sites'], () => searchConsoleService.getAvailableSites(), {
    enabled: isConnected && showWizardOrOnboarding,
    retry: false,
  });

  // GA4 (still returns AxiosResponse)
  const ga4Response = useApiFetch<GA4Property[], void>(
    ['seo-ga4-properties'],
    async () => googleAnalyticsService.listProperties(),
    { enabled: isConnected && showWizardOrOnboarding, retry: false }
  );
  const ga4Properties = (ga4Response.data as GA4Property[]) ?? [];
  const isLoadingGa4 = ga4Response.isLoading;

  const hasProjects = !isLoadingProjects && projects.length > 0;
  const showWizard = !isLoadingProjects && projects.length === 0 && !showOnboarding;

  const handleCreateProject = useCallback(
    async (data: {
      websiteUrl: string;
      name: string;
      siteUrl: string;
      ga4PropertyId: string;
      ga4PropertyName: string;
    }) => {
      setIsCreating(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const domain = data.websiteUrl
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .replace(/\/$/, '');

        const newProject = await seoProjectsService.create({
          organizationId: activeOrgId ?? '00000000-0000-0000-0000-000000000000',
          domain,
          name: data.name,
          siteUrl: data.siteUrl || undefined,
          ga4PropertyId: data.ga4PropertyId || undefined,
          ga4PropertyName: data.ga4PropertyName || undefined,
          createdBy: user?.id ?? '',
        });

        navigate(`/seox/projects/${newProject.shortId}`);
      } catch (err) {
        console.error('[SEOx] Failed to create project:', err);
      } finally {
        setIsCreating(false);
      }
    },
    [navigate, activeOrgId]
  );

  // Show onboarding wizard for first-time users
  // Note: ProjectOnboarding uses MUI components only, so no ChakraProvider wrapper
  if (showWizard || showOnboarding) {
    return (
      <ProjectOnboarding
        connectionStatus={connectionStatus ?? null}
        availableSites={availableSites}
        ga4Properties={ga4Properties}
        isLoadingConnection={isLoadingConnection}
        isLoadingGa4={isLoadingGa4 && isConnected}
        isCreating={isCreating}
        onCreateProject={handleCreateProject}
      />
    );
  }

  return (
    <SEOxProjectListComponent
      projects={projects}
      isLoading={isLoadingProjects}
      onNewProject={() => setShowOnboarding(true)}
    />
  );
};

export default SEOxProjectListContainer;
